import { IdentityService } from '../..'
import { logger } from '../../../../utils/common/logger'
import { DOMUtils } from '../../../../utils/dom'
import { AdDOM } from '../../../ads/ad'
import { AdRefreshDOM } from '../../../ads/ad'
import { AdEvents } from '../../../ads/ad/events'

type ConversationWidgetState = 'none' | 'pinnedComment' | 'polls'
type CustomEventKey = 'click'
type CustomEventCallback = (vf: ViafouraWidget, event: Event) => void
type CustomEventHandlers = Partial<Record<CustomEventKey, CustomEventCallback>>

interface ICommentAttr {
    key: string
    value: string
}

export interface IViafouraWidget {
    el: HTMLElement
    wrapper: string
    vfTag: string
    attrs: ICommentAttr[]
    insertAfterVfTag?: string[]
    adAttrs?: ICommentAttr[]

    handleAdSlotEvent(slotName: string): void
}

export class ViafouraWidget implements IViafouraWidget {
    public adAttrs: ICommentAttr[] = []
    public el: HTMLElement = undefined

    private adEvent: AdEvents = AdEvents.SLOT_ADDED
    private DOM = {
        attrs: {
            class: 'viafoura',
            mobileSize: 'data-comments-ad-size-mobile',
            ref: 'data-comments-ad-ref',
            size: 'data-comments-ad-size',
            target: 'data-comments-ad-target',
            viafouraAd: 'data-ad-comment'
        },
        classes: {
            adContainer: 'ad__inner__container'
        }
    }

    /**
     * Initialize the component and build the widget, if the target class wrapper exists
     *
     * @param wrapper
     * @param attrs
     * @param vfTag
     * @param insertAfterVfTag
     * @param adCount
     * @param customEventHandlers - additional event handlers to be bound to the wrapper
     */
    public constructor(
        public wrapper: string,
        public attrs: ICommentAttr[],
        public vfTag: string,
        public insertAfterVfTag?: string[],
        public adCount = 1,
        public customEventHandlers: CustomEventHandlers = {},
        public skipAds = false
    ) {
        this.loadWidget()
    }

    /**
     * Ad slot handler.  Will be fired when viafoura creates a new ad slot to be added to the widget.
     *
     * @param slotName
     */
    public handleAdSlotEvent(slotName: string): void {
        if (DOMUtils.idElementExists(`${slotName}`)) {
            // target the ad slot provided by viafoura
            const adwrapper = document.getElementById(`${slotName}`)

            // set the correct attributes on the wrapper
            for(const container of IdentityService.DOM.class.adContainers) {
                adwrapper.setAttribute(DOMUtils.attrs.class, `${container}`)
            }

            // create the ad element
            const ad = document.createElement('div')
            ad.setAttribute(AdDOM.attr.location.key, `${this.adCount}`)
            this.adAttrs.forEach((_: any) => {
                ad.setAttribute(_.key, _.value)
            })

            // append the ad element to the ad wrapper
            adwrapper.append(ad)

            logger('info', `added new ad slot for viafoura: ${slotName}, ad counter ${this.adCount}`)

            // fire an event for the new ad slot
            this.dispatchNewAdEvent(slotName)

            // increment the ad count for the next ad
            this.adCount = this.adCount + 1
        }
    }

    /**
     * Dispatch a javascript event indicating that a new ad slot was added
     */
    public dispatchNewAdEvent(slotName: string) {
        const el = DOMUtils.nodes(document.querySelector(`#${slotName} [${this.DOM.attrs.viafouraAd}]`))

        dispatchEvent(new CustomEvent(this.adEvent, {
            detail: {
                el
            }
        }))
    }

    /**
     * Widget-specific:
     */
    public getConversationStarterState(): ConversationWidgetState {
        const element = this.el
        const textSpans = element.querySelectorAll('.vf-conversation-starter_text > span')
        if (textSpans[1]) {
            const text = textSpans[1].textContent.trim()
            if (text === this.attrs.find( (attr) => attr.key === 'description').value) {
                return 'none'
            }
        }
        if (!!this.el.querySelectorAll('.polls__question').length) {
            return 'polls'
        }
        if (!!this.el.querySelectorAll('.vf-editors-pick_container-comment-text').length) {
            return 'pinnedComment'
        }
        return 'none'
    }

    /**
     * Load the initial widget into the DOM
     */
    private loadWidget() {
        this.el = document.querySelector(`[${this.wrapper}]`)

        // only construct the widget html if the element exists on the page
        if (this.el) {
            const widgetElement: HTMLElement = document.createElement('div')

            DOMUtils.addClass(widgetElement, this.DOM.attrs.class)

            const container = document.createElement(this.vfTag)

            this.attrs.forEach((_: any) => {
                container.setAttribute(_.key, _.value)
            })
            widgetElement.appendChild(container)

            if (this.insertAfterVfTag) {
                this.insertAfterVfTag.forEach((el) => {
                    widgetElement.appendChild(document.createElement(el))
                })
            }

            this.el.appendChild(widgetElement)

            if (!this.skipAds) {
                this.initAdAttributes()
            }
            this.initCustomEventHandlers()

            logger('debug', `viafoura widget loaded for wrapper: ${this.wrapper} - appended to element as child`)
        } else {
            logger('debug', `viafoura widget not loaded for wrapper: ${this.wrapper} - wrapper not present`)
        }
    }

    /**
     * Set ad attributes to be used on the ad element
     */
    private initAdAttributes() {
        this.adAttrs = [
            {
                key: DOMUtils.attrs.class,
                value: this.DOM.classes.adContainer
            },
            {
                key: AdDOM.attr.target.key,
                value: this.el.getAttribute(this.DOM.attrs.target)
            },
            {
                key: AdDOM.attr.size.key,
                value: this.el.getAttribute(this.DOM.attrs.size)
            },
            {
                key: AdDOM.attr.mobileSize.key,
                value: this.el.getAttribute(this.DOM.attrs.mobileSize)
            },
            {
                key: AdRefreshDOM.attr.state.key,
                value: this.el.getAttribute(this.DOM.attrs.ref)
            },
            {
                key: `${this.DOM.attrs.viafouraAd}`,
                value: ''
            }
        ]
    }

    /**
     * Initialize custom event handlers, used to run custom javascript on user interactions
     */
    private initCustomEventHandlers() {
        for (const [key, handler] of Object.entries(this.customEventHandlers)) {
            this.el.addEventListener(key, (event: Event) => { handler(this, event) } )
        }
    }
}
